import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Heart } from 'lucide-react';
import { useWishlist } from '../../../pages/wishlist/WishlistContext';
import { useCart } from '../../../contexts/CartContext';
import { CART_CONSTANTS } from '../FuncationsCartAndWishlist/Dto/AddToCardtUtilityDto';
import LoadingSpinner from '../LodingSpiner/LodingSpiner';
import VariantSheetDesktop from '../VariantSheetDesktop/VariantSheetDesktop';
import VariantOptionsModal from '../VariantOptionModal/VariantOptionsModal';
import VariantOptionModalMobile from '../VariantOptionModal/VariantOptionModalMobile';
import '../ProductCardsA/styles/ProductCard.css';
import './styles/ViewAllCards.css';
import './styles/ViewAllCards.mobile.css';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';



interface Product {
  _id: string;
  title: string;
  slug: string;
  price: number;
  description?: string;
  name?: string;
  imageUrl?: string;
  images?: Array<{ url: string; altText?: string; isDefault?: boolean }>;
  discount?: string;
  variants?: any[];
  isActive?: boolean;
  isFeatured?: boolean;
  categories?: Array<{ _id: string; name: string; slug: string }>;
  tags?: string[];
  compareAtPrice?: number;
  quantity?: number;
}

interface ViewAllCardsProps {
  products?: Product[];
  loading?: boolean;
  error?: string | null;
  onAddToCart?: (product: Product) => void;
  onMoreInfo?: (slug: string) => void;
}
const convertVariantsForDisplay = (product: Product) => {
  if (!product.variants || product.variants.length === 0) {
    return [{
      id: '',
      size: product.title,
      price: product.price,
      compareAtPrice: product.compareAtPrice,
      rollLength: 1,
      attributes: {},
      variantImage: undefined,
      variantImages: [],
    }];
  }
  const mainImage = (product.images && product.images.length > 0)
    ? (product.images.find(img => img.isDefault)?.url || product.images[0].url)
    : (product.imageUrl || '/api/placeholder/400/400');
  return product.variants.map((variant: any, idx: number) => {
    const isColorVariant = variant.attributes &&
      Object.keys(variant.attributes).some(key =>
        key.toLowerCase().includes('color') || key.toLowerCase().includes('colour')
      );
    let variantImg;
    if (isColorVariant) {
      variantImg = variant.variantImages?.[0] || variant.variantImage;
      if (idx === 0) {
        variantImg = mainImage;
      } else if (!variantImg || variantImg.trim() === '') {
        variantImg = '/api/placeholder/80/80';
      }
    }
    let variantDisplayName = variant.variantTitle || variant.sku;
    if (variant.attributes) {
      const attributeValues = Object.values(variant.attributes);
      if (attributeValues.length > 0) {
        variantDisplayName = attributeValues[0] as string;
      }
    }
    const baseVariant = {
      id: variant.sku || variant._id,
      size: variantDisplayName,
      price: variant.price || product.price,
      compareAtPrice: variant.compareAtPrice,
      pricePerMeter: (variant.price || product.price) / (variant.weight || 1),
      rollLength: variant.weight || 90,
      attributes: variant.attributes || {},
      variantImage: variant.variantImages?.[0] || variant.variantImage || undefined,
      variantImages: variant.variantImages || [],
      ...variant
    };
    if (isColorVariant) {
      baseVariant.variantImage = variantImg;
    }
    return baseVariant;
  });
};
const ViewAllCards: React.FC<ViewAllCardsProps> = ({ 
  products = [], 
  loading = false, 
  error = null, 
  onAddToCart, 
  onMoreInfo 
}) => {
  const { addItem: addToWishlist, removeItem: removeFromWishlist, isItemInWishlist } = useWishlist();
  const lastWishlistActionRef = useRef<number>(0);
  const { cartItems, updateQuantity, removeFromCart } = useCart();
  const [isMobile, setIsMobile] = useState(false);
  const [modalProduct, setModalProduct] = useState<Product | null>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleWishlistToggle = (product: Product) => {
    const now = Date.now();
    if (now - lastWishlistActionRef.current < 300) return; // Prevent double actions
    lastWishlistActionRef.current = now;

    try {
      const isWishlisted = isItemInWishlist(product._id);
      
      if (isWishlisted) {
        removeFromWishlist(product._id);
        if (isMobile) {
          toast.info('Removed from wishlist', {
            position: 'bottom-right',
            autoClose: 3000,
          });
        }
      } else {
        const hasVariants = Array.isArray(product.variants) && product.variants.length > 0;
        let selectedVariant = null;
        let variantId = '';
        let variantSku = '';
        if (hasVariants && Array.isArray(product.variants)) {
          selectedVariant = product.variants.find(v => v.isDefault) || product.variants[0];
          if (!selectedVariant || (!selectedVariant._id && !selectedVariant.sku) || !selectedVariant.sku || selectedVariant.sku === '') {
            return;
          }
          variantId = selectedVariant._id || selectedVariant.sku;
          variantSku = selectedVariant.sku;
        }
        let primaryImage = product.imageUrl;
        if (!primaryImage && Array.isArray(product.images) && product.images.length > 0 && product.images[0].url) {
          primaryImage = product.images[0].url;
        }
        const productForWishlist = {
          id: product._id,
          _id: product._id,
          title: product.title || product.name || 'Unnamed Product',
          slug: product.slug,
          description: product.description || '',
          price: product.price || 0,
          compareAtPrice: product.compareAtPrice ?? null,
          quantity: product.quantity ?? 0,
          images: primaryImage ? [primaryImage] : [],
          isActive: product.isActive,
          isFeatured: product.isFeatured ?? false,
          hasVariants,
          categories: (product.categories || []).map(c => c.name),
          tags: product.tags || [],
          variantId,
          variantSku,
        } as any;

        addToWishlist(productForWishlist, variantSku);
        if (isMobile) {
          toast.success('Added to wishlist', {
            position: 'bottom-right',
            autoClose: 3000,
          });
        }
      }
    } catch (error) {
    }
  };
  const displayProducts = products.length > 0 ? products : [];
  
  // Memoize modal data to prevent prop changes on every render
  const modalData = useMemo(() => {
    if (!modalProduct) return null;
    return {
      variants: modalProduct.variants ? convertVariantsForDisplay(modalProduct) : [],
      hierarchicalVariants: modalProduct.variants,
      variantHierarchy: (modalProduct as any).variantHierarchy
    };
  }, [modalProduct]);

  if (loading) {
    return (
      <div className="view-all-cards" style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="view-all-cards">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="view-all-cards">
      <div className={`products-grid ${displayProducts.length === 0 ? 'empty' : ''}`}>
        {displayProducts.length > 0 ? (
          displayProducts.map((product) => {
            const name = product.title || product.name || 'Unnamed Product';
            const showPrice = !product.variants || product.variants.length === 0;
            const price = showPrice && product.price ? `₹${Math.round(product.price)}` : '';
            let imgSrc = product.imageUrl;
            if (!imgSrc && Array.isArray(product.images) && product.images.length > 0 && product.images[0].url) {
              imgSrc = product.images[0].url;
            }
            if (!imgSrc) {
              imgSrc = 'https://ik.imagekit.io/raghav11/download.jpg?updatedAt=1757934662868';
            }
            const discount = product.discount || '';
            const cartItem = cartItems.find(item => {
              if (item.productId !== product._id) return false;
              return !item.variantSku || item.variantSku === '';
            });
            const isInCart = !!cartItem;
            const cartQuantity = cartItem?.quantity || 0;
            const handleCartQuantityChange = (newQuantity: number) => {
              if (!cartItem) return;
              if (newQuantity <= 0) {
                removeFromCart(cartItem.productId, cartItem.variantSku, cartItem.variantId);
              } else {
                updateQuantity(cartItem.productId, newQuantity, cartItem.variantSku, cartItem.variantId);
              }
            };
            // ...existing code...
            return (
              <div key={product._id + '-container'}>
                <div 
                  className="product-card"
                  onClick={() => onMoreInfo?.(product.slug || '')}
                  style={{ cursor: 'pointer', position: 'relative' }}
                >
                  {/* Out of Stock Badge */}
                  {product.quantity === 0 && (
                    <div style={{position: 'absolute', top: 8, left: 8, background: '#ccc', color: '#fff', padding: '2px 8px', borderRadius: '8px', fontSize: '0.8rem', zIndex: 20}}>
                      Out of Stock
                    </div>
                  )}
                  <div
                    className="wishlist-icon"
                    title={isItemInWishlist(product._id) ? 'Remove from Wishlist' : 'Add to Wishlist'}
                    onClick={(e) => { e.stopPropagation(); handleWishlistToggle(product); }}
                    onTouchEnd={(e) => { e.stopPropagation(); handleWishlistToggle(product); }}
                    role="button"
                    aria-label={isItemInWishlist(product._id) ? 'Remove from wishlist' : 'Add to wishlist'}
                  >
                    <Heart size={22} color={isItemInWishlist(product._id) ? '#e0245e' : '#000000ff'} strokeWidth={2.2} fill={isItemInWishlist(product._id) ? '#e0245e' : 'none'} />
                  </div>
                  <div className="product-image-container">
                    <img 
                      src={imgSrc} 
                      alt={name}
                      className="product-image"
                    />
                    {discount && (
                      <div className="discount-badge">
                        {discount}
                      </div>
                    )}
                  </div>
                  <h3 className="product-name">{name}</h3>
                  <div className="price-button-container">
                    {showPrice && <div className="product-price">{price}</div>}
                    {product.variants && product.variants.length > 0 ? (
                      <button
                        className="view-options-btn"
                        onClick={(e) => { e.stopPropagation(); setModalProduct(product); }}
                      >
                        View Options
                      </button>
                    ) : isInCart ? (
                      <div className="cart-quantity-controls">
                        <button
                          type="button"
                          className="cart-quantity-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleCartQuantityChange(cartQuantity - 1);
                          }}
                          style={{ cursor: 'pointer', zIndex: 10, position: 'relative' }}
                          aria-label="Decrease quantity"
                        >
                          -
                        </button>
                        <span className="cart-quantity-display">{cartQuantity}</span>
                        <button
                          type="button"
                          className="cart-quantity-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (cartQuantity < CART_CONSTANTS.MAX_QUANTITY) {
                              handleCartQuantityChange(cartQuantity + 1);
                            }
                          }}
                          style={{ cursor: 'pointer', zIndex: 10, position: 'relative' }}
                          aria-label="Increase quantity"
                          disabled={cartQuantity >= CART_CONSTANTS.MAX_QUANTITY}
                        >
                          +
                        </button>
                      </div>
                    ) : (
                      <button 
                        className="add-to-cart-btn"
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          if (onAddToCart) {
                            onAddToCart(product); 
                          }
                        }}
                        style={product.quantity === 0 ? { backgroundColor: '#ccc', color: '#888', cursor: 'not-allowed' } : {}}
                        disabled={product.quantity === 0}
                      >
                        Add
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="no-products">Coming very soon to your doorsteps!!</div>
        )}
      </div>
      {/* Variant Options Modal */}
      {modalProduct && modalData && (
        isMobile ? (
          <VariantOptionModalMobile
            isOpen={!!modalProduct}
            onClose={() => setModalProduct(null)}
            product={modalProduct}
            variants={modalData.variants}
            hierarchicalVariants={modalData.hierarchicalVariants}
            variantHierarchy={modalData.variantHierarchy}
          />
        ) : (
          <VariantOptionsModal
            isOpen={!!modalProduct}
            onClose={() => setModalProduct(null)}
            title={modalProduct ? (modalProduct.title || modalProduct.name || 'Product Options') : ''}
          >
            <VariantSheetDesktop
              product={modalProduct}
              variants={modalData.variants}
              hierarchicalVariants={modalData.hierarchicalVariants}
              variantHierarchy={modalData.variantHierarchy}
              onVariantSelect={() => {}}
            />
          </VariantOptionsModal>
        )
      )}
    </div>
  );
};

export default ViewAllCards;
